import * as THREE from "three";
import { gsap } from "gsap";
import {
  applyBrandCssVariables,
  cloneBrandConfig,
  getCardHref,
  getStoryCtaHref,
  loadStoredBrandConfig,
  resetStoredBrandConfig,
  resolveStorySecondaryHref,
  saveBrandConfig,
} from "../config/brand.js";
import { Scene3D } from "../scenes/Scene3D.js";
import { AnimationController } from "../animation/AnimationController.js";
import { SettingsPage } from "../settings/SettingsPage.js";
import { appendUseCaseContent } from "./useCaseContent.js";

/** Viewport matches desktop nav breakpoint in main.css (880px). */
const HEADER_DOCK_MIN_WIDTH_PX = 880;

function getFloatingCardFlipTarget(layerChild) {
  if (layerChild.classList?.contains("floating-card")) return layerChild;
  return layerChild.querySelector?.(".floating-card") ?? layerChild;
}

/**
 * Imperative Three.js + GSAP hero. Construct with `{ rootEl, loadingEl, brandConfig? }`.
 * Call `dispose()` before unmounting the host (e.g. React route leave).
 */
export class HeroApp {
  constructor({
    rootEl,
    loadingEl,
    brandConfig = loadStoredBrandConfig(),
    siteSettingsEnabled = false,
  } = {}) {
    if (!rootEl || !loadingEl) {
      throw new Error("HeroApp requires rootEl and loadingEl");
    }
    this.rootEl = rootEl;
    this.loadingEl = loadingEl;
    this.brand = brandConfig;
    this.siteSettingsEnabled = Boolean(siteSettingsEnabled);

    this.scene = null;
    this.renderer = null;
    this.camera = null;
    this.scene3D = null;
    this.animationController = null;
    this.settingsPage = null;
    /** @type {HTMLElement | null} */
    this._scrollContentEl = null;
    /** @type {HTMLElement | null} */
    this._floatingCardLayer = null;
    /** Whether feature cards are docked under the site header */
    this._cardSubheaderActive = false;
    this.ambientLight = null;
    this.directionalLight = null;
    this.pointLight = null;
    this._animationFrameId = null;
    this._running = false;
    this._disposed = false;
    /** @type {import("gsap").Timeline | null} */
    this._cardDockTimeline = null;
    /** @type {IntersectionObserver | null} */
    this._storyPanelObserver = null;
    /** @type {HTMLElement[] | null} */
    this._storyPanelElements = null;
    /** @type {Map<Element, number> | null} */
    this._storyIoRatios = null;

    this._onResize = () => this.onResize();
    this._onScroll = () => this.onScroll();
    this._onMouseMove = (e) => this.onMouseMove(e);
    this._onKeyDown = (e) => this.onKeyDown(e);

    this.init();
    this.setupEventListeners();
    this.animate();
  }

  init() {
    this.applyBranding();

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(this.brand.theme.colors.background);
    this.scene.fog = new THREE.Fog(
      this.brand.theme.colors.background,
      this.brand.scene.fog.near,
      this.brand.scene.fog.far,
    );

    const { camera } = this.brand.scene;
    this.camera = new THREE.PerspectiveCamera(
      camera.fov,
      window.innerWidth / window.innerHeight,
      camera.near,
      camera.far,
    );
    this.camera.position.set(...camera.position);

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;

    this.rootEl.appendChild(this.renderer.domElement);
    this.createFloatingCards();
    this.ensureScrollContent();
    this.createBrandOverlay();
    this.createScrollStory();

    this.scene3D = new Scene3D(this.scene, this.camera, this.brand);

    this.animationController = new AnimationController(
      this.scene3D,
      this.camera,
      this.brand,
    );

    this.addLights();
    if (this.siteSettingsEnabled) {
      this.createSettingsPage();
    }

    this.hideLoadingScreen();
    this.onScroll();

    this._running = true;

    console.log(`${this.brand.identity.name} scene initialized successfully.`);
  }

  applyBranding() {
    applyBrandCssVariables(this.brand);

    const loadingTitle = this.loadingEl.querySelector(
      "[data-brand-loading-title]",
    );
    const loadingMessage = this.loadingEl.querySelector(
      "[data-brand-loading-message]",
    );

    if (loadingTitle) loadingTitle.textContent = this.brand.identity.name;
    if (loadingMessage)
      loadingMessage.textContent = this.brand.identity.loadingMessage;

    this.updateBrandOverlay();
    this.syncHeroOverlayStyle();
  }

  syncHeroOverlayStyle() {
    const overlay = this.rootEl?.querySelector(".brand-overlay");
    if (!overlay) return;
    const style =
      this.brand.layout?.heroOverlayStyle === "glass" ? "glass" : "transparent";
    overlay.dataset.overlayStyle = style;
  }

  updateBrandOverlay() {
    this.updateBrandLogo();
    this.setText(".brand-eyebrow", this.brand.identity.eyebrow);
    this.setText(".brand-headline", this.brand.identity.headline);
    this.setText(".brand-subheadline", this.brand.identity.subheadline);
    this.setText(".brand-status", this.brand.identity.status);
    this.setText(".brand-scroll-hint", this.brand.story.scrollHint);
  }

  setText(selector, value) {
    const element = this.rootEl.querySelector(selector);
    if (element) element.textContent = value;
  }

  updateBrandLogo() {
    const mark = this.rootEl?.querySelector(".brand-mark");
    if (!mark) return;
    let logo = mark.querySelector(".brand-logo");
    if (!logo) {
      mark.textContent = "";
      logo = document.createElement("img");
      logo.className = "brand-logo";
      logo.decoding = "async";
      mark.appendChild(logo);
    }
    logo.src = this.brand.identity.logoSrc ?? "/logo.png";
    logo.alt = this.brand.identity.logoAlt ?? this.brand.identity.name;
  }

  ensureScrollContent() {
    if (this._scrollContentEl) return this._scrollContentEl;

    const wrapper = document.createElement("div");
    wrapper.className = "hero-scroll-content";
    this._scrollContentEl = wrapper;
    this.rootEl.appendChild(wrapper);
    return wrapper;
  }

  createBrandOverlay() {
    const overlay = document.createElement("section");
    overlay.className = "brand-overlay";
    overlay.setAttribute(
      "aria-label",
      `${this.brand.identity.name} introduction`,
    );

    const brandMark = document.createElement("div");
    brandMark.className = "brand-mark";

    const eyebrow = document.createElement("p");
    eyebrow.className = "brand-eyebrow";
    eyebrow.textContent = this.brand.identity.eyebrow;

    const headline = document.createElement("h1");
    headline.className = "brand-headline";
    headline.textContent = this.brand.identity.headline;

    const subheadline = document.createElement("p");
    subheadline.className = "brand-subheadline";
    subheadline.textContent = this.brand.identity.subheadline;

    const status = document.createElement("p");
    status.className = "brand-status";
    status.textContent = this.brand.identity.status;

    const scrollHint = document.createElement("a");
    scrollHint.className = "brand-scroll-hint";
    scrollHint.href = "#story-start";
    scrollHint.textContent = this.brand.story.scrollHint;

    overlay.append(
      brandMark,
      eyebrow,
      headline,
      subheadline,
      status,
      scrollHint,
    );
    this.updateBrandLogo();
    this.ensureScrollContent().appendChild(overlay);
  }

  /**
   * @param {boolean} enabled
   */
  setSiteSettingsEnabled(enabled) {
    if (this._disposed) return;
    const on = Boolean(enabled);
    if (this.siteSettingsEnabled === on) return;
    this.siteSettingsEnabled = on;

    if (on) {
      if (!this.settingsPage) {
        this.createSettingsPage();
      }
    } else {
      if (this.settingsPage?.unmount) {
        this.settingsPage.unmount();
        this.settingsPage = null;
      }
      if (window.location.hash === "#settings") {
        const base = window.location.href.replace(/#.*$/, "");
        window.history.replaceState(null, "", base);
      }
    }
  }

  createScrollStory() {
    this._teardownStoryPanelObserver();

    this.rootEl.querySelector("#story")?.remove();

    const story = document.createElement("main");
    story.className = "scroll-story";
    story.id = "story";
    story.setAttribute(
      "aria-label",
      `${this.brand.identity.name} scroll story`,
    );

    const storyAccents = new Set(["primary", "secondary", "accent"]);

    this.brand.story.sections.forEach((section, index) => {
      const panel = document.createElement("article");
      panel.className = "story-panel";
      if (index === 0) panel.id = "story-start";
      panel.style.setProperty("--panel-index", index);

      if (section.accent && storyAccents.has(section.accent)) {
        panel.dataset.accent = section.accent;
      }

      if (section.layout === "useCases") {
        panel.classList.add("story-panel--use-cases");
      }

      const kicker = document.createElement("p");
      kicker.className = "story-kicker";
      kicker.textContent = section.kicker;

      const title = document.createElement("h2");
      title.textContent = section.title;

      const blocks = [kicker, title];

      if (section.outcome) {
        const outcome = document.createElement("p");
        outcome.className = "story-outcome";
        outcome.textContent = section.outcome;
        blocks.push(outcome);
      }

      const body = document.createElement("p");
      body.className = "story-body";
      body.textContent = section.body;
      blocks.push(body);

      if (section.layout === "useCases") {
        panel.append(...blocks);
        appendUseCaseContent(panel, section);
      } else if (Array.isArray(section.bullets) && section.bullets.length > 0) {
        const ul = document.createElement("ul");
        ul.className = "story-bullets";
        for (const line of section.bullets) {
          if (line == null || String(line).trim() === "") continue;
          const li = document.createElement("li");
          li.textContent = String(line);
          ul.appendChild(li);
        }
        if (ul.childElementCount > 0) blocks.push(ul);
      }

      if (section.layout === "useCases") {
        const primaryHref = getStoryCtaHref(section);
        const primaryLabel =
          section.ctaLabel != null ? String(section.ctaLabel).trim() : "";
        const secondaryHref = resolveStorySecondaryHref(section);
        const secondaryLabel =
          section.secondaryLabel != null
            ? String(section.secondaryLabel).trim()
            : "";

        const hasPrimary = Boolean(primaryHref && primaryLabel);
        const hasSecondary = Boolean(secondaryHref && secondaryLabel);

        if (hasPrimary || hasSecondary) {
          const actions = document.createElement("div");
          actions.className = "story-panel__actions";
          if (hasPrimary) {
            const cta = document.createElement("a");
            cta.className = "story-panel__cta";
            cta.href = primaryHref;
            cta.textContent = primaryLabel;
            actions.appendChild(cta);
          }
          if (hasSecondary) {
            const sec = document.createElement("a");
            sec.className = "story-panel__link-secondary";
            sec.href = secondaryHref;
            sec.textContent = secondaryLabel;
            actions.appendChild(sec);
          }
          panel.appendChild(actions);
        }

        story.appendChild(panel);
        return;
      }

      const primaryHref = getStoryCtaHref(section);
      const primaryLabel =
        section.ctaLabel != null ? String(section.ctaLabel).trim() : "";
      const secondaryHref = resolveStorySecondaryHref(section);
      const secondaryLabel =
        section.secondaryLabel != null
          ? String(section.secondaryLabel).trim()
          : "";

      const hasPrimary = Boolean(primaryHref && primaryLabel);
      const hasSecondary = Boolean(secondaryHref && secondaryLabel);

      if (hasPrimary || hasSecondary) {
        const actions = document.createElement("div");
        actions.className = "story-panel__actions";
        if (hasPrimary) {
          const cta = document.createElement("a");
          cta.className = "story-panel__cta";
          cta.href = primaryHref;
          cta.textContent = primaryLabel;
          actions.appendChild(cta);
        }
        if (hasSecondary) {
          const sec = document.createElement("a");
          sec.className = "story-panel__link-secondary";
          sec.href = secondaryHref;
          sec.textContent = secondaryLabel;
          actions.appendChild(sec);
        }
        blocks.push(actions);
      }

      panel.append(...blocks);
      story.appendChild(panel);
    });

    this.ensureScrollContent().appendChild(story);
    this._setupStoryPanelObserver(story);
  }

  _teardownStoryPanelObserver() {
    if (this._storyPanelObserver) {
      this._storyPanelObserver.disconnect();
      this._storyPanelObserver = null;
    }
    this._clearStoryPanelActiveState();
    this._storyIoRatios = null;
    this._storyPanelElements = null;
  }

  _clearStoryPanelActiveState() {
    this._storyPanelElements?.forEach((p) =>
      p.classList.remove("story-panel--active"),
    );
    if (document.documentElement.dataset.storyPanel != null) {
      delete document.documentElement.dataset.storyPanel;
    }
  }

  _setStoryPanelActiveFromIntersection() {
    if (!this._storyPanelElements?.length || !this._storyIoRatios) return;

    const THRESHOLD_MIN = 0.35;
    let bestIdx = -1;
    let bestR = 0;
    this._storyPanelElements.forEach((panel, idx) => {
      const r = this._storyIoRatios.get(panel) ?? 0;
      if (r > bestR) {
        bestR = r;
        bestIdx = idx;
      }
    });
    const on = bestR >= THRESHOLD_MIN && bestIdx >= 0;
    this._storyPanelElements.forEach((p, i) => {
      p.classList.toggle("story-panel--active", on && i === bestIdx);
    });
    if (on) {
      document.documentElement.dataset.storyPanel = String(bestIdx);
    } else if (document.documentElement.dataset.storyPanel != null) {
      delete document.documentElement.dataset.storyPanel;
    }
  }

  /**
   * @param {HTMLElement} storyEl
   */
  _setupStoryPanelObserver(storyEl) {
    const panels = storyEl.querySelectorAll(".story-panel");
    if (!panels.length) return;

    this._storyPanelElements = Array.from(panels);
    this._storyIoRatios = new Map();
    const threshold = Array.from({ length: 21 }, (_, i) => i / 20);

    this._storyPanelObserver = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          this._storyIoRatios.set(e.target, e.intersectionRatio);
        }
        this._setStoryPanelActiveFromIntersection();
      },
      { root: null, rootMargin: "0px", threshold },
    );

    this._storyPanelElements.forEach((el) => {
      this._storyPanelObserver.observe(el);
    });
  }

  createFloatingCards() {
    this._floatingCardLayer?.remove();
    this._floatingCardLayer = null;
    document
      .getElementById("site-header-hero-cards")
      ?.querySelectorAll(".floating-card-layer")
      .forEach((el) => el.remove());

    const layer = document.createElement("aside");
    layer.className = "floating-card-layer";
    layer.setAttribute("aria-label", "Interactive feature cards");

    this.brand.featureCards.forEach((card, index) => {
      const href = getCardHref(card);
      const wrap =
        href && href !== "#"
          ? document.createElement("a")
          : document.createElement("div");
      if (href && href !== "#") {
        wrap.className = "floating-card-link";
        wrap.href = href;
        wrap.tabIndex = 0;
        wrap.style.setProperty("--card-x", card.position.x);
        wrap.style.setProperty("--card-y", card.position.y);
        wrap.style.setProperty("--card-index", index);
        wrap.setAttribute("aria-label", `${card.title}: view details`);
      }

      const featureCard = document.createElement("article");
      featureCard.className = "floating-card";
      featureCard.tabIndex = href && href !== "#" ? -1 : 0;
      if (href === "#" || !href) {
        featureCard.style.setProperty("--card-x", card.position.x);
        featureCard.style.setProperty("--card-y", card.position.y);
      }
      featureCard.style.setProperty("--card-index", index);
      featureCard.dataset.accent = card.accent;

      const stat = document.createElement("span");
      stat.className = "floating-card-stat";
      stat.textContent = card.stat;

      const kicker = document.createElement("p");
      kicker.className = "floating-card-kicker";
      kicker.textContent = card.kicker;

      const title = document.createElement("h3");
      title.textContent = card.title;

      const body = document.createElement("p");
      body.className = "floating-card-body";
      body.textContent = card.body;

      featureCard.append(stat, kicker, title, body);
      if (wrap.tagName === "A") {
        wrap.appendChild(featureCard);
        layer.appendChild(wrap);
      } else {
        layer.appendChild(featureCard);
      }
    });

    this.rootEl.appendChild(layer);
    this._floatingCardLayer = layer;
    this.syncFloatingCardSubheader();
  }

  /** Dock measurement helpers (kept in sync with syncFloatingCardSubheader). */
  _dockScrollThresholds() {
    const y = window.scrollY;
    const enterAt = Math.max(100, window.innerHeight * 0.12);
    const exitAt = Math.max(40, window.innerHeight * 0.05);
    return { y, enterAt, exitAt };
  }

  _nextDockedFlag(current, y, enterAt, exitAt) {
    let next = current;
    if (!next && y >= enterAt) next = true;
    else if (next && y <= exitAt) next = false;
    return next;
  }

  _isWideEnoughForHeaderDock() {
    return window.matchMedia(
      `(min-width: ${HEADER_DOCK_MIN_WIDTH_PX}px)`,
    ).matches;
  }

  /** Homepage + desktop: dock into #site-header-hero-cards instead of a second bar. */
  _shouldDockCardsInHeader() {
    return (
      window.location.pathname === "/" && this._isWideEnoughForHeaderDock()
    );
  }

  _getCardLayerDockParent() {
    if (this._shouldDockCardsInHeader()) {
      const slot = document.getElementById("site-header-hero-cards");
      if (slot) return slot;
    }
    return this.rootEl;
  }

  /**
   * Keep the layer under the correct parent when docked/undocked or viewport crosses breakpoint.
   */
  _syncFloatingCardLayerParent() {
    const el = this._floatingCardLayer;
    if (!el || this._disposed) return;

    const target = this._cardSubheaderActive
      ? this._getCardLayerDockParent()
      : this.rootEl;
    if (el.parentElement !== target) {
      target.appendChild(el);
    }
    document.documentElement.classList.toggle(
      "hero-cards-docked-in-header",
      this._cardSubheaderActive && this._shouldDockCardsInHeader(),
    );
  }

  /** Dock floating cards under the sticky header after light scroll (hysteresis). */
  syncFloatingCardSubheader() {
    if (!this._floatingCardLayer || this._disposed) return;

    /* Narrow viewports: no header dock or fixed strip - links are in the hamburger menu. */
    if (!this._isWideEnoughForHeaderDock()) {
      if (this._cardDockTimeline) {
        this._cardDockTimeline.kill();
        this._cardDockTimeline = null;
      }
      if (this._cardSubheaderActive) {
        this._animateCardDockToggle(false);
      } else {
        document.documentElement.classList.remove("has-floating-card-subheader");
        document.documentElement.classList.remove("hero-cards-docked-in-header");
        this._floatingCardLayer?.classList.remove("is-card-subheader");
        this._syncFloatingCardLayerParent();
      }
      return;
    }

    const { y, enterAt, exitAt } = this._dockScrollThresholds();
    const next = this._nextDockedFlag(
      this._cardSubheaderActive,
      y,
      enterAt,
      exitAt,
    );

    if (next === this._cardSubheaderActive) {
      this._syncFloatingCardLayerParent();
      return;
    }

    this._animateCardDockToggle(next);
  }

  /**
   * FLIP transition between scattered hero cards and the docked subheader strip.
   * @param {boolean} nextActive
   */
  _animateCardDockToggle(nextActive) {
    const layer = this._floatingCardLayer;
    if (!layer || this._disposed) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this._cardSubheaderActive = nextActive;
      layer.classList.toggle("is-card-subheader", nextActive);
      document.documentElement.classList.toggle(
        "has-floating-card-subheader",
        nextActive,
      );
      const parent = nextActive ? this._getCardLayerDockParent() : this.rootEl;
      parent.appendChild(layer);
      document.documentElement.classList.toggle(
        "hero-cards-docked-in-header",
        nextActive && this._shouldDockCardsInHeader(),
      );
      return;
    }

    const items = Array.from(layer.children);
    const targets = items.map(getFloatingCardFlipTarget);

    if (this._cardDockTimeline) {
      this._cardDockTimeline.kill();
      this._cardDockTimeline = null;
      targets.forEach((el) => {
        gsap.set(el, { clearProps: "transform,transformOrigin" });
      });
    }

    const firstRects = targets.map((el) => el.getBoundingClientRect());

    this._cardSubheaderActive = nextActive;
    layer.classList.toggle("is-card-subheader", nextActive);
    document.documentElement.classList.toggle(
      "has-floating-card-subheader",
      nextActive,
    );

    const dockParent = nextActive ? this._getCardLayerDockParent() : this.rootEl;
    dockParent.appendChild(layer);
    document.documentElement.classList.toggle(
      "hero-cards-docked-in-header",
      nextActive && this._shouldDockCardsInHeader(),
    );

    void layer.offsetHeight;

    const lastRects = targets.map((el) => el.getBoundingClientRect());

    const tl = gsap.timeline({
      onComplete: () => {
        targets.forEach((el) => {
          gsap.set(el, { clearProps: "transform,transformOrigin" });
        });
        this._cardDockTimeline = null;
      },
    });
    this._cardDockTimeline = tl;

    targets.forEach((el, i) => {
      const f = firstRects[i];
      const l = lastRects[i];
      const dx = f.left - l.left;
      const dy = f.top - l.top;
      const sx = l.width > 0.5 ? f.width / l.width : 1;
      const sy = l.height > 0.5 ? f.height / l.height : 1;

      gsap.set(el, {
        transformOrigin: "0 0",
        x: dx,
        y: dy,
        scaleX: sx,
        scaleY: sy,
      });

      tl.to(
        el,
        {
          x: 0,
          y: 0,
          scaleX: 1,
          scaleY: 1,
          duration: 0.5,
          ease: "power3.out",
        },
        i * 0.045,
      );
    });
  }

  createSettingsPage() {
    if (this.settingsPage) return;
    this.settingsPage = new SettingsPage({
      getBrandConfig: () => this.brand,
      onPreview: (nextBrand) => this.applyBrandConfig(nextBrand),
      onSave: (nextBrand) => {
        saveBrandConfig(nextBrand);
        this.applyBrandConfig(nextBrand);
      },
      onReset: (defaultConfig) => {
        resetStoredBrandConfig();
        this.applyBrandConfig(defaultConfig);
      },
    });

    this.settingsPage.mount(this.rootEl);
  }

  applyBrandConfig(nextBrand) {
    this.brand = cloneBrandConfig(nextBrand);
    this.applyBranding();
    this.applySceneBranding();
    this.createFloatingCards();
    this.createScrollStory();
    this.onScroll();
  }

  applySceneBranding() {
    const { colors } = this.brand.theme;
    const { fog, lights } = this.brand.scene;

    this.scene.background = new THREE.Color(colors.background);
    this.scene.fog = new THREE.Fog(colors.background, fog.near, fog.far);

    if (this.scene3D) this.scene3D.applyBrandConfig(this.brand);
    if (this.animationController) {
      this.animationController.applyBrandConfig(this.brand);
    }

    if (this.ambientLight)
      this.ambientLight.intensity = lights.ambientIntensity;
    if (this.directionalLight) {
      this.directionalLight.intensity = lights.directionalIntensity;
    }
    if (this.pointLight) {
      this.pointLight.color.set(colors.secondary);
      this.pointLight.intensity = lights.pointIntensity;
      this.pointLight.distance = lights.pointDistance;
    }
  }

  addLights() {
    const { colors } = this.brand.theme;
    const { lights } = this.brand.scene;
    this.ambientLight = new THREE.AmbientLight(
      0xffffff,
      lights.ambientIntensity,
    );
    this.scene.add(this.ambientLight);

    this.directionalLight = new THREE.DirectionalLight(
      0xffffff,
      lights.directionalIntensity,
    );
    this.directionalLight.position.set(5, 5, 5);
    this.directionalLight.castShadow = true;
    this.scene.add(this.directionalLight);

    this.pointLight = new THREE.PointLight(
      colors.secondary,
      lights.pointIntensity,
      lights.pointDistance,
    );
    this.pointLight.position.set(0, 2, 0);
    this.scene.add(this.pointLight);
  }

  hideLoadingScreen() {
    const loadingScreen = this.loadingEl;
    if (loadingScreen) {
      gsap.to(loadingScreen, {
        opacity: 0,
        duration: 0.5,
        onComplete: () => {
          loadingScreen.classList.add("hidden");
        },
      });
    }
  }

  setupEventListeners() {
    window.addEventListener("resize", this._onResize);
    window.addEventListener("scroll", this._onScroll, { passive: true });
    document.addEventListener("mousemove", this._onMouseMove);
    document.addEventListener("keydown", this._onKeyDown);
  }

  removeEventListeners() {
    window.removeEventListener("resize", this._onResize);
    window.removeEventListener("scroll", this._onScroll);
    document.removeEventListener("mousemove", this._onMouseMove);
    document.removeEventListener("keydown", this._onKeyDown);
  }

  onResize() {
    if (!this.camera || !this.renderer) return;
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.onScroll();
  }

  onScroll() {
    const scrollableHeight =
      document.documentElement.scrollHeight - window.innerHeight;
    const progress =
      scrollableHeight > 0 ? window.scrollY / scrollableHeight : 0;
    const clampedProgress = Math.min(Math.max(progress, 0), 1);

    document.documentElement.style.setProperty(
      "--scroll-progress",
      clampedProgress.toFixed(4),
    );

    if (this.animationController) {
      this.animationController.handleScroll(clampedProgress);
    }

    this.syncFloatingCardSubheader();
  }

  onMouseMove(e) {
    if (this.animationController) {
      const x = (e.clientX / window.innerWidth) * 2 - 1;
      const y = -(e.clientY / window.innerHeight) * 2 + 1;
      this.animationController.handleMouseMove(x, y);
    }
  }

  onKeyDown(e) {
    if (this.animationController) {
      switch (e.key.toLowerCase()) {
        case " ":
          this.animationController.triggerAnimation();
          break;
        case "r":
          this.animationController.resetAnimation();
          break;
        case "m":
          this.animationController.toggleMaterials();
          break;
      }
    }
  }

  animate() {
    if (!this._running) return;
    this._animationFrameId = window.requestAnimationFrame(() => this.animate());

    if (this.animationController) {
      this.animationController.update();
    }

    if (this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }
  }

  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this._running = false;

    document.documentElement.classList.remove("has-floating-card-subheader");
    document.documentElement.classList.remove("hero-cards-docked-in-header");
    this._cardSubheaderActive = false;

    if (this._cardDockTimeline) {
      this._cardDockTimeline.kill();
      this._cardDockTimeline = null;
    }
    if (this._floatingCardLayer) {
      Array.from(this._floatingCardLayer.children)
        .map(getFloatingCardFlipTarget)
        .forEach((el) => {
          gsap.set(el, { clearProps: "transform,transformOrigin" });
        });
      this._floatingCardLayer.remove();
      this._floatingCardLayer = null;
    }
    document
      .getElementById("site-header-hero-cards")
      ?.querySelectorAll(".floating-card-layer")
      .forEach((el) => el.remove());

    this._teardownStoryPanelObserver();

    if (this._animationFrameId != null) {
      cancelAnimationFrame(this._animationFrameId);
      this._animationFrameId = null;
    }

    this.removeEventListeners();

    gsap.killTweensOf(this.loadingEl);

    if (this.settingsPage?.unmount) {
      this.settingsPage.unmount();
      this.settingsPage = null;
    }

    if (this.animationController?.dispose) {
      this.animationController.dispose();
    }
    this.animationController = null;

    if (this.scene3D?.dispose) {
      this.scene3D.dispose();
    }
    this.scene3D = null;

    if (this.renderer) {
      this.renderer.dispose();
      if (this.renderer.domElement?.parentNode) {
        this.renderer.domElement.remove();
      }
    }
    this.renderer = null;

    if (this.scene) {
      this.scene.clear();
    }
    this.scene = null;
    this.camera = null;
    this.ambientLight = null;
    this.directionalLight = null;
    this.pointLight = null;

    this._scrollContentEl = null;

    if (this.rootEl) {
      this.rootEl.replaceChildren();
    }
  }
}
